import { Suspense, useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
  BrowserRouter,
} from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import TagManager from 'react-gtm-module';
import * as Sentry from '@sentry/react';
import { Analytics } from '@vercel/analytics/react';

import App from './App';
import { ENVIRONMENT, GTM_ID, SENTRY_CONFIG } from './config-global';
import { CONSTANT_VARIABLES } from './constants/constants-variables';
import 'src/utils/analytics';

// ----------------------------------------------------------------------

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const tagManagerArgs = {
  gtmId: GTM_ID,
};

const sentryArgs = {
  dsn: SENTRY_CONFIG.dsn,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllInputs: false,
      blockAllMedia: false,
    }),
  ],
  env: SENTRY_CONFIG.env,
  enabled:
    ENVIRONMENT === CONSTANT_VARIABLES.PRODUCTION || ENVIRONMENT === CONSTANT_VARIABLES.STAGE,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.5,

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
};

TagManager.initialize(tagManagerArgs);
if ([CONSTANT_VARIABLES.PRODUCTION, CONSTANT_VARIABLES.STAGE].includes(ENVIRONMENT)) {
  Sentry.init(sentryArgs);
}

const CustomApp = () => {
  const location = useLocation();

  useEffect(() => {
    const dataLayerArgs = {
      dataLayer: {
        event: 'virtualPageview',
        pagePath: document.location.pathname,
        pageTitle: document?.title,
        pageUrl: `${document.location.protocol}//${document.location.hostname}${document.location.pathname}${document.location.search}`,
      },
    };

    TagManager.dataLayer(dataLayerArgs);
  }, [location]);

  return <App />;
};

root.render(
  <HelmetProvider>
    <BrowserRouter>
      <Suspense>
        <CustomApp />
        {[CONSTANT_VARIABLES.PRODUCTION].includes(ENVIRONMENT) && <Analytics />}
      </Suspense>
    </BrowserRouter>
  </HelmetProvider>,
);
